// frontend/src/components/planner/LinkToForecastModal.jsx
// Modal for manually linking a Plaid transaction to an existing forecast instance

import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Link2, Calendar, DollarSign, Loader2 } from 'lucide-react';
import { format, parseISO, subDays, addDays } from 'date-fns';
import api from '../../utils/api';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(Math.abs(amount));
}

export default function LinkToForecastModal({ txn, onClose, onSuccess }) {
  const [forecastItems, setForecastItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadForecastItems();
  }, []);

  async function loadForecastItems() {
    try {
      setLoading(true);
      // Fetch forecast instances within a reasonable window of the Plaid transaction date
      const txnDate = txn.date;
      const startDate = format(subDays(parseISO(txnDate + 'T00:00:00'), 14), 'yyyy-MM-dd');
      const endDate = format(addDays(parseISO(txnDate + 'T00:00:00'), 14), 'yyyy-MM-dd');
      
      const response = await api.get(`/api/planner/forecast-instances`, {
        params: { startDate, endDate, unmatched: true }
      });
      
      if (response.data.success) {
        setForecastItems(response.data.instances || []);
      }
    } catch (err) {
      console.error('Failed to load forecast items:', err);
      setError('Failed to load forecast items');
    } finally {
      setLoading(false);
    }
  }

  const filteredItems = useMemo(() => {
    if (!searchQuery) return forecastItems;
    const q = searchQuery.toLowerCase();
    return forecastItems.filter(item => 
      item.name.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q)
    );
  }, [forecastItems, searchQuery]);

  async function handleLink(forecastItem) {
    try {
      setLinking(true);
      setError(null);
      
      await api.post('/api/planner/manual-match', {
        plaidTransactionId: txn.plaid_transaction_id,
        scheduledTransactionId: forecastItem.id,
        amountAction: 'update_instance'
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to link transaction:', err);
      setError(err.response?.data?.error || 'Failed to link transaction');
      setLinking(false);
    }
  }

  // Calculate date difference for display
  function dateDiff(forecastDate) {
    const txnD = new Date(txn.date + 'T00:00:00');
    const fD = new Date(forecastDate + 'T00:00:00');
    const diff = Math.round((fD - txnD) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'same day';
    if (diff > 0) return `${diff}d later`;
    return `${Math.abs(diff)}d earlier`;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white w-full h-full sm:h-auto sm:max-h-[80vh] sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-t-2xl shadow-xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 bg-purple-600 rounded-t-2xl sm:rounded-t-xl flex-shrink-0 z-10">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Link to Existing Forecast
            </h3>
            <p className="text-purple-200 text-base sm:text-sm mt-1">
              {txn.merchant_name || txn.name} · {formatCurrency(txn.amount)} · {format(parseISO(txn.date + 'T00:00:00'), 'MMM d, yyyy')}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search forecast items..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              autoFocus
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-base sm:text-sm text-red-700">
            {error}
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
              <span className="ml-2 text-gray-500">Loading forecast items...</span>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No unmatched forecast items found</p>
              <p className="text-base sm:text-sm mt-1">within ±14 days of this transaction</p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleLink(item)}
                  disabled={linking}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-purple-50 transition-colors text-left disabled:opacity-50 group min-h-[44px]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 group-hover:text-purple-700 truncate">
                      {item.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-base sm:text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(parseISO(item.scheduled_date + 'T00:00:00'), 'MMM d, yyyy')}
                      </span>
                      <span className="text-purple-600 font-medium">{dateDiff(item.scheduled_date)}</span>
                      {item.account_name && (
                        <span className="truncate">{item.account_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="font-medium text-gray-700">
                      {formatCurrency(item.amount)}
                    </span>
                    <Link2 className="w-4 h-4 text-gray-300 group-hover:text-purple-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 p-3 border-t border-gray-200 bg-gray-50 sm:rounded-b-xl text-base sm:text-xs text-gray-500 text-center flex-shrink-0">
          Showing unmatched forecast items within ±14 days · {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}
